import {HalContext, HalImage, HalPainterImageCompositorRef} from "@cm/hal"
import {getHalAddressMode} from "@cm/webgl2"
import {Box2Like} from "@cm/math"
import {AddressMode} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/basic-definitions"
import {z} from "zod"

export const borderMode = z.union([AddressMode, z.literal("renormalize")])
export type BorderMode = z.infer<typeof borderMode>

export class ConvolveHelperHal {
    constructor(private halContext: HalContext) {
        this.compositor = this.createCompositor()
    }

    dispose() {
        this.compositor.release()
    }

    convolve(args: {source: HalImage; target: HalImage; kernelImage: HalImage; targetMipLevel?: number; targetRegion?: Box2Like; borderMode?: BorderMode}) {
        const borderMode = args.borderMode ?? "renormalize"
        this.compositor.ref.paint({
            target: args.target,
            sourceImages: [args.source, args.kernelImage],
            parameters: {
                u_targetMipLevel: {type: "int", value: args.targetMipLevel ?? 0},
                u_kernelWidth: {type: "int", value: args.kernelImage.width},
                u_kernelHeight: {type: "int", value: args.kernelImage.height},
                u_addressMode: {type: "int", value: getHalAddressMode(borderMode === "renormalize" ? "border" : borderMode)},
                u_renormalizeKernel: {type: "int", value: args.borderMode === "renormalize" ? 1 : 0},
            },
            options: {
                targetRegion: args.targetRegion,
                targetMipLevel: args.targetMipLevel,
            },
        })
    }

    private createCompositor() {
        return this.halContext.createHalPainterImageCompositor(
            `
            uniform int u_targetMipLevel;
            uniform int u_kernelWidth;
            uniform int u_kernelHeight;
            uniform int u_addressMode;
            uniform int u_renormalizeKernel;

            vec4 computeColor(ivec2 targetPixel) {
                vec4 color = vec4(0);
                ivec2 kernelOfs = (ivec2(u_kernelWidth, u_kernelHeight) - 1) / 2;
                float kernelSum = 0.0;
                for (int ky = 0; ky < u_kernelHeight; ky++) {
                    for (int kx = 0; kx < u_kernelWidth; kx++) {
                        float k = texelChannelFetchLod1(ivec2(kx, ky), 0, 0);
                        ivec2 sourcePixelIndex = targetPixel + ivec2(kx, ky) - kernelOfs;
                        vec4 i = texelFetchLod0(sourcePixelIndex, u_targetMipLevel, u_addressMode);
                        color += i * k;
                        if (!isBorderTexelLod(0, sourcePixelIndex, u_targetMipLevel)) {
                            kernelSum += k;
                        }
                    }
                }
                if (u_renormalizeKernel != 0) {
                    color /= kernelSum;
                }
                return color;
            }
        `,
        )
    }

    private readonly compositor: HalPainterImageCompositorRef
}
